import { useFetch } from "../../../hooks/api";
import useDefaultState from "../../../hooks/useDefaultState";
import { Col, Row } from "react-bootstrap";
import { Button } from "../../../components/core/basic/Button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../utils/api";
import { useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import BudgetProgramLog from "../../../components/custom/programs/budget/BudgetProgramLog";
import SelectField from "../../../components/core/basic/SelectField";
import { successToast, errorToast } from "../../../utils/toasts";
import Loading from "../../../components/core/blocks/Loading";
import { css } from "../../../../styled-system/css";
import { useModuleEnabled } from "../../../redux/selectors/modules";

const RulesSection = ({ advertisement, readOnly }) => {
  const { t } = useTranslation(["advertising", "common"]);
  const queryClient = useQueryClient();
  const hasMultiOrgRulesModuleEnabled = useModuleEnabled("MULTI_ORG_RULES");
  const { data: programsData, isLoading: isProgramDataLoading } = useFetch(
    ["programs"],
    "/programs/",
    {
      engine: "budget",
      all_user_orgs: hasMultiOrgRulesModuleEnabled,
    },
    { select: (data) => data?.data?.results ?? [] },
  );

  const { data: adPlanData, isLoading: isAdPlanDataLoading } = useFetch(
    ["media-plan", advertisement.id],
    "/api/gvads/advproduct/",
    {
      advertisement: advertisement.id,
    },
    {
      select: (d) => d.data,
      enabled: !!advertisement?.id,
    },
  );
  const adPlanParams = useMemo(() => {
    return adPlanData?.ad_control_fields ?? {};
  }, [adPlanData]);

  const [budgetProgram, setBudgetProgram] = useDefaultState(adPlanParams.budget_program || -1);

  const updateProgram = useMutation({
    mutationFn: async (program) => {
      await api.put(`/api/gvads/advproduct/`, {
        id: advertisement.id,
        ad_control_fields: {
          budget_program: program === -1 ? null : program,
        },
        state: advertisement.state,
        custom_api_request: "update_advertisement",
      });
    },

    onSuccess: () => {
      successToast({ message: t(($) => $.advertising["Updating Media Plan"]) });
      queryClient.invalidateQueries({
        queryKey: ["media-plan", advertisement.id],
      });
    },

    onError: (error) => {
      errorToast({ message: t(($) => $.advertising["Failed to Update Media Plan"]), error });
    },
  });

  const programOptions =
    programsData?.map((program) => {
      return { value: program.id, label: program.name };
    }) ?? [];
  programOptions.unshift({
    value: -1,
    label: t(($) => $.advertising["No Media Plan Rules"]),
  });

  const isDirty = budgetProgram !== (adPlanParams.budget_program || -1);

  return (
    <section>
      <h5 className="fw-bold pb-4 pt-1">{t(($) => $.advertising["Media Plan Rules"])}</h5>
      {isAdPlanDataLoading || isProgramDataLoading ?
        <div className={css({ margin: "auto" })}>
          <Loading fullPage={false} height={"150px"} />
        </div>
      : <Row>
          <Col xs={12} lg={6} className="d-flex align-items-center">
            <>
              <div className="flex-fill pe-3">
                <SelectField
                  value={budgetProgram}
                  onChange={setBudgetProgram}
                  options={programOptions}
                  isLoading={!programOptions || isProgramDataLoading}
                  disabled={readOnly}
                />
              </div>
              <Button
                onPress={() => {
                  updateProgram.mutate(budgetProgram);
                }}
                isDisabled={
                  !isDirty ||
                  updateProgram.isPending ||
                  isAdPlanDataLoading ||
                  advertisement.serving_status === "ENDED" ||
                  readOnly
                }
              >
                {t(($) => $.common.actions.Save)}
              </Button>
            </>
          </Col>
        </Row>
      }
      <Row>
        <Col xs={12} lg={6}>
          <p className="fs-5 mt-3">
            <Trans
              i18nKey={($) =>
                $.advertising[
                  "Use Media Plan Rules to control the budget for this advertisement based on performance. Click here to create a new <mediaPlanRulesLink>Media Plan Rules</mediaPlanRulesLink>."
                ]
              }
              t={t}
              components={{ mediaPlanRulesLink: <Link to="/user/advertising/settings" /> }}
            />
          </p>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <BudgetProgramLog advertisementId={advertisement.id} />
        </Col>
      </Row>
    </section>
  );
};

export default RulesSection;
